import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../static/query-keys";

import {
  createMedicalTreatmentRecordV3,
  deleteMedicalTreatmentRecordV1,
  updateMedicalTreatmentRecordV1,
} from "@/lib/data/requests/matters/medical-treatment-record";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

export const useCreateMedicalTreatmentRecord = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMedicalTreatmentRecordV3>[0]) =>
      createMedicalTreatmentRecordV3(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentRecords: [
                ...oldData.data.medicalTreatmentRecords,
                {
                  ...data,
                  id: "optimistic",
                  dateCreated: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
                },
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: async (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
      toastAPIErrorMessage(error, "Error creating medical treatment record");
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Medical treatment record created successfully");
      }
    },
  });
};

export const useUpdateMedicalTreatmentRecord = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMedicalTreatmentRecordV1>[0]) =>
      updateMedicalTreatmentRecordV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentRecords: [
                ...oldData.data.medicalTreatmentRecords.map((record) =>
                  record.id === data.id ? { ...record, ...data } : record,
                ),
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating medical treatment record");
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Medical treatment record updated successfully");
      }
    },
  });
};

export const useDeleteMedicalTreatmentRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMedicalTreatmentRecordV1>[0]) =>
      deleteMedicalTreatmentRecordV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentRecords:
                oldData.data.medicalTreatmentRecords.filter(
                  (record) => record.id !== data.id,
                ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting medical treatment record");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Medical treatment record deleted successfully");
    },
  });
};
